import { buildAgentsSearchUrl } from "~/lib/marketplace/constants";
import { MARKETING_CUSTOM_AGENT_DEVELOPMENT } from "~/lib/inbound-gpt/paths";
import type { MoonlightCategoryId } from "~/lib/marketplace/moonlight-home";

export const SITEGLUE_AI_EMPLOYEES_LINE = "Explore live agents in action.";

/** Top nav mega menu — Roles dropdown */
export const SITEGLUE_ROLES_NAV_LABEL = "Roles";
export const SITEGLUE_ROLES_MEGA_TITLE = "Agent Roles";
export const SITEGLUE_FEATURED_ROLES_LABEL = "Featured Roles";
export const SITEGLUE_ROLES_REQUEST_LABEL = "Request a custom role";
export const SITEGLUE_ROLES_SEE_ALL_LABEL = "See all Agent Roles";

export type SiteGlueIndustryChip = {
  id: string;
  label: string;
  /** Moonlight category id when filtering by vertical. */
  category?: MoonlightCategoryId;
  /** Marketplace industry slug when filtering specialty (dental, PT, etc.). */
  industry?: string;
};

export type SiteGlueRoleChip = {
  id: string;
  label: string;
  role?: string;
  jobTitles?: string[];
};

/** Industry filter chips for the AI Employees showcase. */
export const SITEGLUE_AI_EMPLOYEE_INDUSTRIES: SiteGlueIndustryChip[] = [
  { id: "all", label: "All" },
  { id: "healthcare", label: "Healthcare", category: "healthcare" },
  { id: "dental", label: "Dental", industry: "dental" },
  { id: "physical_therapy", label: "Physical Therapy", industry: "physical_therapy" },
  { id: "chiropractic", label: "Chiropractic", industry: "chiropractic" },
  { id: "legal", label: "Legal", category: "legal" },
  { id: "real_estate", label: "Real Estate", category: "real_estate" },
  { id: "home_services", label: "Home Services", category: "home_services" },
  { id: "retail", label: "Retail", category: "ecommerce" },
  { id: "education", label: "Education", category: "education" },
  { id: "financial", label: "Financial", category: "financial" },
  { id: "automotive", label: "Automotive", category: "automotive" },
];

/** Role filter chips for the AI Employees showcase. */
export const SITEGLUE_AI_EMPLOYEE_ROLES: SiteGlueRoleChip[] = [
  {
    id: "receptionist",
    label: "Receptionist",
    role: "receptionist",
    jobTitles: ["Front Desk Receptionist", "Receptionist"],
  },
  {
    id: "patient_coordinator",
    label: "Patient Coordinator",
    role: "patient_coordinator",
    jobTitles: ["Online Patient Coordinator", "Patient Coordinator"],
  },
  {
    id: "scheduler",
    label: "Scheduler",
    role: "appointment_setter",
    jobTitles: ["Scheduling Specialist", "Scheduling Coordinator", "Scheduler"],
  },
  {
    id: "sales",
    label: "Sales",
    role: "sales",
    jobTitles: ["Inbound Sales Rep", "Sales Assistant", "Sales"],
  },
  {
    id: "customer_support",
    label: "Customer Support",
    role: "customer_service",
    jobTitles: ["Online Support Agent", "Customer Support Agent", "Customer Support"],
  },
  {
    id: "marketing",
    label: "Marketing",
    jobTitles: ["Marketing", "Marketing Assistant"],
  },
  {
    id: "billing",
    label: "Billing",
    jobTitles: ["Billing", "Billing Specialist"],
  },
  {
    id: "recruiting",
    label: "Recruiting",
    jobTitles: ["Recruiting", "Recruiter", "Recruiting Coordinator"],
  },
];

/** Featured roles in the AI Employees mega menu. */
export const SITEGLUE_FEATURED_AI_EMPLOYEES = [
  {
    label: "Patient Coordinator",
    description: "Intake, scheduling, and patient questions with human handoff.",
    href: buildAgentsSearchUrl({
      jobTitles: ["Online Patient Coordinator", "Patient Coordinator"],
    }),
  },
  {
    label: "Front Desk Receptionist",
    description: "Greet callers and visitors, answer FAQs, route to your team.",
    href: buildAgentsSearchUrl({ jobTitles: ["Front Desk Receptionist"] }),
  },
  {
    label: "Scheduling Specialist",
    description: "Book and reschedule appointments across channels.",
    href: buildAgentsSearchUrl({
      jobTitles: ["Scheduling Specialist", "Scheduling Coordinator"],
      role: "appointment_setter",
    }),
  },
  {
    label: "Customer Support Agent",
    description: "Resolve common questions and escalate when it matters.",
    href: buildAgentsSearchUrl({
      jobTitles: ["Online Support Agent", "Customer Support Agent"],
      role: "customer_service",
    }),
  },
  {
    label: "Sales Assistant",
    description: "Qualify leads and book demos while keeping humans in the loop.",
    href: buildAgentsSearchUrl({
      jobTitles: ["Inbound Sales Rep", "Sales Assistant"],
      role: "sales",
    }),
  },
] as const;

export const SITEGLUE_AI_EMPLOYEES_BROWSE_INDUSTRY_HREF = "/agents#industries";
export const SITEGLUE_AI_EMPLOYEES_BROWSE_ROLE_HREF = "/agents#roles";
export const SITEGLUE_AI_EMPLOYEES_REQUEST_HREF = MARKETING_CUSTOM_AGENT_DEVELOPMENT;
export const SITEGLUE_AI_EMPLOYEES_SEE_ALL_HREF = "/agents";

/** Public gallery + homepage hero hard cap — never show more than this many cards. */
export const SITEGLUE_PUBLIC_AGENTS_DISPLAY_LIMIT = 6;

/** Channel keys shown on showcase cards (order matters) — labels from shared marketing channels. */
export const SITEGLUE_SHOWCASE_CHANNELS = [
  { key: "call", label: "Phone" },
  { key: "aiChat", label: "Website Chat" },
  { key: "sms", label: "SMS" },
  { key: "email", label: "Email" },
  { key: "video", label: "Video Calls" },
  { key: "whatsapp", label: "WhatsApp" },
  { key: "messenger", label: "Messenger" },
] as const;
